<template>
  <component
    :is="as"
    :class="[
      'tw:mx-0.5',
      isBaseButton &&
        'tw:text-denim-500! tw:hover:text-denim-800! tw:p-1! tw:py-0.5!',
    ]"
    data-testid="social-mention-engagement-item"
    :dense="isBaseButton"
    :flat="isBaseButton"
    :icon="baseIconMap"
    :icon-class="twMerge('tw:text-[12px]!', iconClass)"
    new-design
    new-icon
    @click.stop="emit('click')"
  >
    <span
      v-if="isMentionIcon"
      :class="twMerge('tw:text-[12px]!', iconClass)"
    >
      @
    </span>
    <BaseIcon
      v-if="shouldRenderIcon && baseIconMap"
      :class="iconClass"
      :icon="baseIconMap"
    />
    <span
      v-if="!hideEngagement"
      class="tw:font-bold tw:text-[10px] tw:leading-[10px] tw:text-inherit"
    >
      {{ formattedValue }}
    </span>
    <slot />
  </component>
</template>

<script setup lang="ts">
import { type DesignSystemIcons } from "design-system/types";
import { twMerge } from "tailwind-merge";
import { type Component, computed } from "vue";

import { BaseButton, BaseIcon } from "shared/components/base";
import { suffix } from "shared/helpers/number";
import { type Maybe } from "shared/types";

// These are legacy values before we migrated the icons to use the design system.
// This is here for compatibility.
type LegacySocialEngagementIcon =
  | "bookmark"
  | "comments"
  | "likes"
  | "mention"
  | "play-circle-filled"
  | "quoted"
  | "reshare"
  | "share"
  | "updownvote"
  | "views"
  | "youtube_like";

type SocialEngagementIcon = DesignSystemIcons | LegacySocialEngagementIcon;

interface Props {
  as?: Component | string;
  hideEngagement?: boolean;
  icon: SocialEngagementIcon;
  iconClass?: string;
  modelValue?: number | string;
}

const props = withDefaults(defineProps<Props>(), {
  as: BaseButton,
  iconClass: undefined,
  modelValue: 0,
});

const emit = defineEmits<{
  click: [];
}>();

const baseIconMap = computed<Maybe<DesignSystemIcons>>(() => {
  switch (props.icon) {
    case "likes":
      return "heart-filled";
    case "comments":
      return "social-stream";
    case "mention":
      return undefined;
    case "youtube_like":
      return "thumbs-up";
    default:
      return props.icon;
  }
});

const isBaseButton = computed<boolean>(() => props.as === BaseButton);

const isMentionIcon = computed<boolean>(() => props.icon === "mention");

const shouldRenderIcon = computed<boolean>(
  () => props.as !== BaseButton && props.icon && !isMentionIcon.value
);

const formattedValue = computed<string>(() => suffix(props.modelValue || 0));
</script>
